import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import f1Service from '../../../components/dashboards/services/f1Service';

/** 
 * F1 Teammate Battle Component
 * Head-to-head comparison of teammates across all 10 teams
 * Shows qualifying battles, race finishes, points, and more
 */
const F1TeammateBattle = ({ season = 2025 }) => {
  const [teammatePairs, setTeammatePairs] = useState([]);
  const [battleStats, setBattleStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTeam, setExpandedTeam] = useState(null);

  useEffect(() => {
    const fetchTeammateData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get all drivers
        const driversResponse = await f1Service.getCurrentDrivers();
        if (!driversResponse?.response) {
          throw new Error('No driver data available');
        }

        // Group drivers by team
        const teamGroups = {};
        driversResponse.response.forEach(driver => {
          const teamName = driver.teams?.[0]?.team?.name || 'Unknown';
          if (!teamGroups[teamName]) {
            teamGroups[teamName] = [];
          }
          teamGroups[teamName].push(driver);
        });

        // Create teammate pairs (only teams with 2 drivers)
        const pairs = Object.entries(teamGroups)
          .filter(([_, drivers]) => drivers.length === 2)
          .map(([teamName, drivers]) => ({
            team: teamName,
            teamId: drivers[0].teams?.[0]?.team?.id,
            teamLogo: drivers[0].teams?.[0]?.team?.logo,
            driver1: drivers[0],
            driver2: drivers[1]
          }));

        setTeammatePairs(pairs);

        // Fetch battle stats for each pair
        await fetchBattleStats(pairs);

        setLoading(false);
      } catch (err) {
        console.error('Error fetching teammate data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchTeammateData();
  }, [season]);

  const fetchBattleStats = async (pairs) => {
    const stats = {};

    // Get driver standings for points comparison
    const standingsResponse = await f1Service.getDriverStandings(season);
    const standings = standingsResponse?.response || [];

    // Get completed races for qualifying and race comparisons
    const completedRacesResponse = await f1Service.getCompletedRaces(season);
    const completedRaces = completedRacesResponse?.response || [];

    for (const pair of pairs) {
      const driver1Id = pair.driver1.id;
      const driver2Id = pair.driver2.id;

      // Get standings data
      const driver1Standing = standings.find(s => s.driver?.id === driver1Id);
      const driver2Standing = standings.find(s => s.driver?.id === driver2Id);

      let quali1Wins = 0;
      let quali2Wins = 0;
      let race1Wins = 0;
      let race2Wins = 0;
      let race1Positions = [];
      let race2Positions = [];

      // Compare each completed race
      for (const race of completedRaces) {
        try {
          // Get qualifying results (starting grid as proxy)
          const gridResponse = await f1Service.getStartingGrid(race.id);
          if (gridResponse?.response) {
            const d1Grid = gridResponse.response.find(r => r.driver?.id === driver1Id);
            const d2Grid = gridResponse.response.find(r => r.driver?.id === driver2Id);

            if (d1Grid && d2Grid) {
              if (d1Grid.position < d2Grid.position) quali1Wins++;
              else if (d2Grid.position < d1Grid.position) quali2Wins++;
            }
          }

          // Get race results
          const raceResponse = await f1Service.getRaceResults(race.id);
          if (raceResponse?.response) {
            const d1Result = raceResponse.response.find(r => r.driver?.id === driver1Id);
            const d2Result = raceResponse.response.find(r => r.driver?.id === driver2Id);

            if (d1Result && d2Result) {
              const pos1 = d1Result.position || 999;
              const pos2 = d2Result.position || 999;

              race1Positions.push(pos1);
              race2Positions.push(pos2);

              if (pos1 < pos2) race1Wins++;
              else if (pos2 < pos1) race2Wins++;
            }
          }
        } catch (err) {
          // Skip this race if data not available
          console.log(`Skipping race ${race.id} for comparison`);
        }
      }

      // Calculate average finishing positions
      const avgPos1 = race1Positions.length > 0
        ? (race1Positions.reduce((a, b) => a + b, 0) / race1Positions.length).toFixed(1)
        : 'N/A';
      const avgPos2 = race2Positions.length > 0
        ? (race2Positions.reduce((a, b) => a + b, 0) / race2Positions.length).toFixed(1)
        : 'N/A';

      stats[pair.team] = {
        driver1: {
          points: driver1Standing?.points || 0,
          wins: driver1Standing?.wins || 0,
          podiums: driver1Standing?.podiums || 0,
          qualiWins: quali1Wins,
          raceWins: race1Wins,
          avgPosition: avgPos1
        },
        driver2: {
          points: driver2Standing?.points || 0,
          wins: driver2Standing?.wins || 0,
          podiums: driver2Standing?.podiums || 0,
          qualiWins: quali2Wins,
          raceWins: race2Wins,
          avgPosition: avgPos2
        },
        totalRaces: completedRaces.length
      };
    }

    setBattleStats(stats);
  };

  // Get team color
  const getTeamColor = (teamName) => {
    const colors = f1Service.getTeamColors(teamName);
    return colors.primary;
  };

  // Get team code
  const getTeamCode = (teamName) => {
    return f1Service.getTeamCode(teamName);
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
        <div className="p-4 border-b border-white/5">
          <div className="h-4 w-40 bg-white/10 rounded animate-pulse" />
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="h-64 bg-white/10 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white/[0.02] border border-red-500/20 rounded-sm p-4">
        <p className="text-xs text-red-400 font-mono">Error: {error}</p>
      </div>
    );
  }

  // Empty state
  if (teammatePairs.length === 0) {
    return (
      <div className="bg-white/[0.02] border border-white/5 rounded-sm p-8 text-center">
        <p className="text-xs text-gray-600 font-mono uppercase tracking-wider">
          No Teammate Data Available
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-white/5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider font-mono">
            Teammate Battle - {season}
          </h3>
          <span className="text-[10px] text-gray-600 font-mono uppercase tracking-wider">
            Head to Head
          </span>
        </div>
      </div>

      {/* Team Battle Cards */}
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {teammatePairs.map((pair, index) => {
          const teamColor = getTeamColor(pair.team);
          const teamCode = getTeamCode(pair.team);
          const stats = battleStats[pair.team] || {};
          const isExpanded = expandedTeam === pair.team;

          return (
            <motion.div
              key={pair.team}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05, duration: 0.2 }}
              className="cursor-pointer"
              onClick={() => setExpandedTeam(isExpanded ? null : pair.team)}
            >
              {/* Team Card */}
              <div
                className="border rounded-sm transition-all duration-200 hover:border-opacity-100"
                style={{
                  backgroundColor: `${teamColor}08`,
                  borderColor: isExpanded ? teamColor : `${teamColor}40`
                }}
              >
                {/* Team Header */}
                <div
                  className="p-3 border-b"
                  style={{ borderColor: `${teamColor}20`, backgroundColor: `${teamColor}10` }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-bold uppercase tracking-wider" style={{ color: teamColor }}>
                      {teamCode}
                    </div>
                    {pair.teamLogo && (
                      <img src={pair.teamLogo} alt={pair.team} className="h-5 w-auto opacity-70" />
                    )}
                  </div>
                  <div className="text-[10px] text-gray-500 font-mono mb-2 truncate">
                    {pair.team}
                  </div>
                  {/* Team Car Image */}
                  {f1Service.getTeamCarImage(pair.team, season) && (
                    <div className="mt-2 -mx-3 -mb-3 h-20 overflow-hidden">
                      <img
                        src={f1Service.getTeamCarImage(pair.team, season)}
                        alt={`${pair.team} Car`}
                        className="w-full h-full object-contain object-center opacity-40 hover:opacity-60 transition-opacity"
                      />
                    </div>
                  )}
                </div>

                {/* Driver Comparison */}
                <div className="p-3">
                  {/* Driver 1 */}
                  <div className="mb-3">
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex-1">
                        <div className="text-xs font-bold text-white">
                          {pair.driver1.name}
                        </div>
                        <div className="text-[10px] text-gray-500 font-mono">
                          #{pair.driver1.number}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-white tabular-nums">
                          {stats.driver1?.points || 0}
                        </div>
                        <div className="text-[9px] text-gray-600 uppercase">PTS</div>
                      </div>
                    </div>
                  </div>

                  {/* VS Divider */}
                  <div className="relative h-px bg-white/10 my-3">
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-black px-2">
                      <span className="text-[10px] text-gray-600 font-bold uppercase tracking-wider">VS</span>
                    </div>
                  </div>

                  {/* Driver 2 */}
                  <div>
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex-1">
                        <div className="text-xs font-bold text-white">
                          {pair.driver2.name}
                        </div>
                        <div className="text-[10px] text-gray-500 font-mono">
                          #{pair.driver2.number}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-white tabular-nums">
                          {stats.driver2?.points || 0}
                        </div>
                        <div className="text-[9px] text-gray-600 uppercase">PTS</div>
                      </div>
                    </div>
                  </div>

                  {/* Battle Stats */}
                  {stats.driver1 && stats.driver2 && (
                    <div className="mt-4 pt-3 border-t border-white/5 space-y-2">
                      {/* Qualifying */}
                      <div className="flex items-center justify-between text-[10px] font-mono">
                        <span className="font-bold text-white tabular-nums">{stats.driver1.qualiWins}</span>
                        <span className="text-gray-600 uppercase tracking-wider">Quali</span>
                        <span className="font-bold text-white tabular-nums">{stats.driver2.qualiWins}</span>
                      </div>

                      {/* Race */}
                      <div className="flex items-center justify-between text-[10px] font-mono">
                        <span className="font-bold text-white tabular-nums">{stats.driver1.raceWins}</span>
                        <span className="text-gray-600 uppercase tracking-wider">Race</span>
                        <span className="font-bold text-white tabular-nums">{stats.driver2.raceWins}</span>
                      </div>

                      {/* Wins */}
                      {(stats.driver1.wins > 0 || stats.driver2.wins > 0) && (
                        <div className="flex items-center justify-between text-[10px] font-mono">
                          <span className="font-bold text-white tabular-nums">{stats.driver1.wins}</span>
                          <span className="text-gray-600 uppercase tracking-wider">Wins</span>
                          <span className="font-bold text-white tabular-nums">{stats.driver2.wins}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Expanded Details */}
                {isExpanded && stats.driver1 && stats.driver2 && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="border-t border-white/5 p-3 space-y-2 text-[11px] font-mono"
                  >
                    <div className="text-[10px] text-gray-600 uppercase tracking-wider mb-2">
                      Detailed Stats
                    </div>

                    <div className="flex justify-between">
                      <span className="text-gray-500">Avg Position</span>
                      <div className="flex gap-2">
                        <span className="text-white">{stats.driver1.avgPosition}</span>
                        <span className="text-gray-700">-</span>
                        <span className="text-white">{stats.driver2.avgPosition}</span>
                      </div>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-gray-500">Podiums</span>
                      <div className="flex gap-2">
                        <span className="text-white">{stats.driver1.podiums}</span>
                        <span className="text-gray-700">-</span>
                        <span className="text-white">{stats.driver2.podiums}</span>
                      </div>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-gray-500">Races Analyzed</span>
                      <span className="text-white">{stats.totalRaces}</span>
                    </div>
                  </motion.div>
                )}

                {/* Team Color Bar */}
                <div
                  className="h-1 transition-all duration-200"
                  style={{ backgroundColor: teamColor }}
                />
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-white/5 bg-white/[0.01]">
        <div className="flex items-center justify-between text-[10px] text-gray-600 font-mono uppercase tracking-wider">
          <span>Total Teams</span>
          <span className="text-white font-bold">{teammatePairs.length}</span>
        </div>
      </div>
    </div>
  );
};

export default F1TeammateBattle;
